import React, { useEffect, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import { Box } from '@mui/material';

import { useGetChainLpcLppSupplierBreakdown } from '~/hooks/apis/price-parity/useGetChainLpcLppSupplierBreakdown';
import { useGetChainLpcLppSupplierChains } from '~/hooks/apis/price-parity/useGetChainLpcLppSupplierChains';

import type { ChainLpcLppSupplierPropertyGroup } from '~/types/services/priceParrot';

import DateRangeSlider from '../PriceParrotParityTool/DateRangeSlider';
import {
  buildCalendarDatesInclusive,
  getParityTrendFullWindowStart,
} from '../PriceParrotParityTool/parityTrendDateRange';

import ChainAndRegionPicker from './ChainAndRegionPicker';
import FilterControls from './FilterControls';
import HotelRatioTrendModal from './HotelRatioTrendModal';
import PropertiesList from './PropertiesList';
import RollupSummaryTiles from './RollupSummaryTiles';
import { DisplaySortMode, HOTSPOT_RATIO_THRESHOLD, PROPERTIES_PAGE_SIZE } from './constants';
import { lpcToLppRatio } from './ratio';
import { computeRollup, emptyStateMessage, hasLppSupplier, presetKeyFor } from './utils';

export default function PriceParrotChainLpcLppRatioDashboard() {
  const { calendarDates, initialRange } = useMemo(() => {
    const end = dayjs().startOf('day');
    const start = getParityTrendFullWindowStart(end);
    const dates = buildCalendarDatesInclusive(start, end);
    const len = dates.length;
    const range: [number, number] = len > 1 ? [Math.max(0, len - 365), len - 1] : [0, 0];
    return { calendarDates: dates, initialRange: range };
  }, []);

  const [committedIndices, setCommittedIndices] = useState<[number, number]>(initialRange);
  const dateFrom = calendarDates[committedIndices[0]] ?? '';
  const dateTo = calendarDates[committedIndices[1]] ?? '';

  const [sort, setSort] = useState<DisplaySortMode>('views');
  const [mixedSppOnly, setMixedSppOnly] = useState(true);
  const [hotspotsOnly, setHotspotsOnly] = useState(true);
  const [region, setRegion] = useState<string | null>(null);
  const [trendGroup, setTrendGroup] = useState<ChainLpcLppSupplierPropertyGroup | null>(null);
  const [page, setPage] = useState(1);
  const [selectedChain, setSelectedChain] = useState<string | null>(null);

  const {
    data: chainsListData,
    isLoading: isChainsLoading,
    isError: isChainsError,
    error: chainsError,
  } = useGetChainLpcLppSupplierChains(dateFrom, dateTo);

  const chainOptions = useMemo(
    () => [...(chainsListData?.chains ?? [])].sort((a, b) => a.localeCompare(b)),
    [chainsListData?.chains],
  );

  // Auto-pick the first chain (alphabetically, since this list carries no
  // volume stat to rank by) when none is selected, or when the current
  // selection drops out of the list.
  useEffect(() => {
    if (!chainOptions.length) {
      return;
    }
    if (selectedChain && chainOptions.includes(selectedChain)) {
      return;
    }
    setSelectedChain(chainOptions[0]);
  }, [chainOptions, selectedChain]);

  // 'ratio_asc' has no server-side equivalent, so request the default order
  // and re-sort client-side below instead.
  const {
    data: breakdownData,
    isFetching: isBreakdownFetching,
    isError: isBreakdownError,
    error: breakdownError,
  } = useGetChainLpcLppSupplierBreakdown(
    selectedChain ?? undefined,
    dateFrom,
    dateTo,
    sort === 'ratio_asc' ? 'views' : sort,
  );

  const activePresetKey = useMemo(
    () => presetKeyFor(committedIndices, calendarDates.length),
    [committedIndices, calendarDates.length],
  );

  const handlePeriodPreset = (days: number) => {
    const maxIdx = calendarDates.length - 1;
    if (maxIdx < 0) {
      return;
    }
    const fromIdx = Math.max(0, maxIdx - (days - 1));
    setCommittedIndices([fromIdx, maxIdx]);
  };

  // No bedbank competition means no LPC:LPP ratio to show.
  const lppMixedProperties = useMemo(
    () => (breakdownData?.properties ?? []).filter(hasLppSupplier),
    [breakdownData?.properties],
  );

  // Sourced from the unfiltered set so the dropdown stays stable once a region is selected.
  const regionOptions = useMemo<Array<string>>(() => {
    const regions = new Set<string>();
    for (const group of breakdownData?.properties ?? []) {
      if (group.region) {
        regions.add(group.region);
      }
    }
    return Array.from(regions).sort((a, b) => a.localeCompare(b));
  }, [breakdownData?.properties]);

  useEffect(() => {
    setRegion(null);
  }, [selectedChain]);

  useEffect(() => {
    if (region && !regionOptions.includes(region)) {
      setRegion(null);
    }
  }, [region, regionOptions]);

  const isFiltered = mixedSppOnly || region != null || hotspotsOnly;

  const displayedProperties = useMemo(() => {
    let properties = mixedSppOnly ? lppMixedProperties : (breakdownData?.properties ?? []);
    if (region) {
      properties = properties.filter((g) => g.region === region);
    }
    if (hotspotsOnly) {
      properties = properties.filter((g) => {
        const ratio = lpcToLppRatio(g);
        return ratio != null && ratio < HOTSPOT_RATIO_THRESHOLD;
      });
    }
    if (sort === 'ratio_asc') {
      // Properties with no LPP bookings have no ratio to rank by — push them last.
      properties = [...properties].sort((a, b) => {
        const ratioA = lpcToLppRatio(a) ?? Infinity;
        const ratioB = lpcToLppRatio(b) ?? Infinity;
        return ratioA - ratioB;
      });
    }
    return properties;
  }, [mixedSppOnly, lppMixedProperties, breakdownData?.properties, region, hotspotsOnly, sort]);

  // breakdownData.rollup is computed server-side over the full, unfiltered
  // property list, so it only matches what's on screen when isFiltered is
  // false — otherwise recompute from displayedProperties instead.
  const rollup = useMemo(
    () => (isFiltered ? computeRollup(displayedProperties) : breakdownData?.rollup),
    [isFiltered, displayedProperties, breakdownData?.rollup],
  );

  const pageCount = Math.max(1, Math.ceil(displayedProperties.length / PROPERTIES_PAGE_SIZE));
  const visibleProperties = displayedProperties.slice((page - 1) * PROPERTIES_PAGE_SIZE, page * PROPERTIES_PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [selectedChain, dateFrom, dateTo, sort, mixedSppOnly, hotspotsOnly, region]);

  return (
    <Box>
      <ChainAndRegionPicker
        chainOptions={chainOptions}
        selectedChain={selectedChain}
        onChainChange={setSelectedChain}
        isChainsLoading={isChainsLoading}
        isChainsError={isChainsError}
        chainsError={chainsError}
        regionOptions={regionOptions}
        region={region}
        onRegionChange={setRegion}
      />

      <Box>
        <FilterControls
          activePresetKey={activePresetKey}
          onPeriodPresetChange={handlePeriodPreset}
          sort={sort}
          onSortChange={setSort}
          mixedSppOnly={mixedSppOnly}
          onMixedSppOnlyChange={setMixedSppOnly}
          hotspotsOnly={hotspotsOnly}
          onHotspotsOnlyChange={setHotspotsOnly}
        />

        <Box sx={{ mb: 2 }}>
          <DateRangeSlider
            dates={calendarDates}
            range={committedIndices}
            onRangeChange={(next) => setCommittedIndices(next as [number, number])}
          />
        </Box>

        <RollupSummaryTiles rollup={rollup} />

        <PropertiesList
          isFetching={isBreakdownFetching}
          isError={isBreakdownError}
          error={breakdownError}
          displayedProperties={displayedProperties}
          visibleProperties={visibleProperties}
          emptyMessage={emptyStateMessage(mixedSppOnly, hotspotsOnly, region)}
          page={page}
          pageCount={pageCount}
          onPageChange={setPage}
          onShowTrend={setTrendGroup}
        />
      </Box>

      <HotelRatioTrendModal
        chainName={selectedChain}
        group={trendGroup}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onClose={() => setTrendGroup(null)}
      />
    </Box>
  );
}
